import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getCategoryData,
  getVehiclesModels,
  getVehiclesMakeByCategoryId,
  getVehicleModelsByCategoryAndMake,
  getServicesByCategoryId,
} from "../services/api";

const VehicleRateLookup = () => {
  const navigate = useNavigate();
  const suggestionRefs = useRef([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [allModels, setAllModels] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [makes, setMakes] = useState([]);
  const [selectedMake, setSelectedMake] = useState("");
  const [makeSearch, setMakeSearch] = useState("");
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState("");
  const [services, setServices] = useState([]);
  const [highlightedMakeIndex, setHighlightedMakeIndex] = useState(-1);

  useEffect(() => {
    fetchCategories();
    fetchAllModels();
  }, []);

  useEffect(() => {
    if (!selectedCategory) {
      setMakes([]);
      setSelectedMake("");
      setModels([]);
      setSelectedModel("");
      setServices([]);
      return;
    }

    fetchMakesByCategory(selectedCategory);
  }, [selectedCategory]);

  useEffect(() => {
    if (!selectedCategory || !selectedMake) {
      setModels([]);
      setSelectedModel("");
      return;
    }

    fetchModelsByCategoryAndMake(selectedCategory, selectedMake);
  }, [selectedCategory, selectedMake]);

  useEffect(() => {
    if (!selectedCategory) {
      setServices([]);
      return;
    }

    fetchServicesByCategory(selectedCategory);
  }, [selectedCategory]);

  useEffect(() => {
    if (highlightedMakeIndex >= 0 && suggestionRefs.current[highlightedMakeIndex]) {
      suggestionRefs.current[highlightedMakeIndex].scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [highlightedMakeIndex]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const result = await getCategoryData();
      if (result.success) {
        setCategories(result.data || []);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllModels = async () => {
    try {
      const result = await getVehiclesModels();
      if (result.success) {
        setAllModels(result.data || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchMakesByCategory = async (categoryId) => {
    try {
      setLoading(true);
      const result = await getVehiclesMakeByCategoryId(categoryId);
      if (result.success) {
        setMakes(result.data || []);
      } else {
        setMakes([]);
      }
    } catch (error) {
      console.error(error);
      setMakes([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchModelsByCategoryAndMake = async (categoryId, makeId) => {
    try {
      setLoading(true);
      const result = await getVehicleModelsByCategoryAndMake(categoryId, makeId);
      if (result.success) {
        setModels(result.data || []);
      } else {
        setModels([]);
      }
    } catch (error) {
      console.error(error);
      setModels([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchServicesByCategory = async (categoryId) => {
    try {
      setLoading(true);
      const result = await getServicesByCategoryId(categoryId);
      if (result.success) {
        setServices(result.data || []);
      } else {
        setServices([]);
      }
    } catch (error) {
      console.error(error);
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (event) => {
    const value = event.target.value;
    setSelectedCategory(value);
    setSelectedMake("");
    setSelectedModel("");
    setMakeSearch("");
    setHighlightedMakeIndex(-1);
  };

  const handleMakeChange = (event) => {
    const value = event.target.value;
    setSelectedMake(value);
    setSelectedModel("");
    setMakeSearch("");
    setHighlightedMakeIndex(-1);
  };

  const handleModelChange = (event) => {
    setSelectedModel(event.target.value);
    setMakeSearch("");
    setHighlightedMakeIndex(-1);
  };

  const handleMakeSearchChange = (event) => {
    const value = event.target.value;
    setMakeSearch(value);
    setHighlightedMakeIndex(-1);

    if (!value.trim()) {
      setSelectedMake("");
      setSelectedCategory("");
      setSelectedModel("");
      setModels([]);
      return;
    }

    const match = allModels.find(
      (model) => model.name.toLowerCase() === value.trim().toLowerCase()
    );

    if (match) {
      applyModelSelection(match);
    } else {
      setSelectedMake("");
      setSelectedCategory("");
      setSelectedModel("");
      setModels([]);
    }
  };

  const handleMakeSearchKeyDown = (event) => {
    if (!filteredMakes.length) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedMakeIndex((prev) =>
        prev < filteredMakes.length - 1 ? prev + 1 : 0
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedMakeIndex((prev) =>
        prev > 0 ? prev - 1 : filteredMakes.length - 1
      );
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const selectedMatch =
        highlightedMakeIndex >= 0
          ? filteredMakes[highlightedMakeIndex]
          : filteredMakes[0];

      if (selectedMatch) {
        applyModelSelection(selectedMatch);
      }
      return;
    }

    if (event.key === "Escape") {
      setMakeSearch("");
      setHighlightedMakeIndex(-1);
      setSelectedMake("");
      setSelectedCategory("");
      setSelectedModel("");
      setModels([]);
    }
  };

  const applyModelSelection = (model) => {
    setSelectedMake(String(model.make_id));
    setSelectedCategory(String(model.category_id));
    setSelectedModel(String(model.id));
    setMakeSearch(model.name);
  };

  const filteredMakes = allModels.filter((model) =>
    model.name.toLowerCase().includes((makeSearch || "").trim().toLowerCase())
  );

  const showMakeSuggestions =
    makeSearch.trim().length > 0 && !selectedCategory && !selectedMake && !selectedModel;

  const handleSuggestedMakeClick = (model) => {
    applyModelSelection(model);
  };

  return (
    <div className="container mt-2 bg-white rounded shadow-sm p-4">
      <form className="form-horizontal" onSubmit={(e) => e.preventDefault()}>
        <fieldset>
          <legend>
            <center>
              <h3 className="mt-2 bg-secondary text-white rounded py-2">Vehicle Rate Search</h3>
            </center>
          </legend>

          <div className="d-flex justify-content-end mb-3">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => navigate("/booking")}
            >
              Back to Booking
            </button>
          </div>

          <div className="row g-3 mb-4">
            <div className="col-md-12">
              <label className="form-label fw-bold">Search Model / Car Name</label>
              <input
                type="text"
                className="form-control"
                placeholder="Type model name / car name"
                value={makeSearch}
                onChange={handleMakeSearchChange}
                onKeyDown={handleMakeSearchKeyDown}
                autoComplete="off"
                spellCheck={false}
              />
              {showMakeSuggestions && filteredMakes.length > 0 && (
                <div
                  className="mt-2 border rounded bg-white shadow-sm"
                  style={{ maxHeight: "220px", overflowY: "auto" }}
                >
                  {filteredMakes.map((model, index) => (
                    <button
                      type="button"
                      key={model.id}
                      ref={(el) => (suggestionRefs.current[index] = el)}
                      className={`d-block w-100 text-start border-0 px-3 py-2 ${
                        highlightedMakeIndex === index ? "bg-light text-dark" : "bg-transparent"
                      }`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleSuggestedMakeClick(model)}
                      style={{ minHeight: "38px" }}
                    >
                      {model.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="row align-items-center my-3">
            <div className="col">
              <hr />
            </div>
            <div className="col-auto">
              <span className="text-uppercase small fw-bold text-muted">OR</span>
            </div>
            <div className="col">
              <hr />
            </div>
          </div>

          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <label className="form-label fw-bold">Category</label>
              <select
                className="form-select"
                value={selectedCategory}
                onChange={handleCategoryChange}
              >
                <option value="">Select category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label fw-bold">Make</label>
              <select
                className="form-select"
                value={selectedMake}
                onChange={handleMakeChange}
                disabled={!selectedCategory}
              >
                <option value="">Select make</option>
                {makes.map((make) => (
                  <option key={make.id} value={make.id}>
                    {make.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label fw-bold">Model</label>
              <select
                className="form-select"
                value={selectedModel}
                onChange={handleModelChange}
                disabled={!selectedMake}
              >
                <option value="">Select model</option>
                {models.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : selectedCategory ? (
            <div className="border rounded bg-light p-3">
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div>
                  <h5 className="mb-1">
                    {categories.find((cat) => String(cat.id) === String(selectedCategory))?.name || "Selected Category"}
                  </h5>
                  <small className="text-muted">
                    {selectedMake ? `Make: ${makes.find((make) => String(make.id) === String(selectedMake))?.name || ""}` : ""}
                    {selectedModel ? ` / Model: ${models.find((model) => String(model.id) === String(selectedModel))?.name || ""}` : ""}
                  </small>
                </div>
              </div>

              {services.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-bordered table-sm mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>Service</th>
                        <th className="text-end">Rate (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {services.map((service, index) => (
                        <tr key={service.id || index}>
                          <td>{index + 1}</td>
                          <td>{service.name}</td>
                          <td className="text-end fw-bold">
                            {Number(service.amount || 0).toLocaleString("en-IN")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-muted">No service rates available for this category.</div>
              )}
            </div>
          ) : (
            <div className="text-muted">Select a category, make, and model to view rates.</div>
          )}
        </fieldset>
      </form>
    </div>
  );
};

export default VehicleRateLookup;
